/**
 * What the palette offers, derived from the registry.
 *
 * The registry deliberately contains **everything** placeable it can find in the
 * fragment catalog — 31 rows on a real partner, including fragments with no
 * manifest. The palette is narrower on purpose:
 *
 * - **Declared core components only.** A fragment with no manifest has no prop
 *   schema, so the inspector would have nothing to show and required params could
 *   not be filled. Partner fragments (declared or inferred) join the palette in
 *   Sprint 6.3, with an "inferred parameters" badge.
 * - **`native.html` is not offered.** It is the legacy container and the raw
 *   escape hatch, not something to reach for while building; inserting one before
 *   the inspector can edit its `html` prop would place a block that compiles to
 *   nothing.
 *
 * Layout fragments never reach here — `buildRegistry` already excludes them,
 * because a layout wraps the template (`layout_key`), it is not a block.
 */
import { resolveBlockDisplayLabel } from './labels';
import { BlockDefinition, Registry } from './registry';

/** Type prefix marking a component we ship and support in the palette. */
export const CORE_TYPE_PREFIX = 'core.';

export type PaletteGroupId = 'layout' | 'content' | 'custom';

export interface PaletteItem {
  type: string;
  label: string;
  /** Which pictogram stands for this block in the palette. */
  icon: PaletteIconId;
  /** Containers accept children; the palette shows them as such. */
  container: boolean;
  /** Params were guessed from the source; the palette says so. */
  inferred: boolean;
  /** The underlying fragment key, shown on hover; null for a primitive. */
  fragmentKey: string | null;
}

/**
 * The pictogram a palette tile shows. A name for the *shape* rather than for one
 * component, because partner fragments have to reach these too: `partials/xxxl/footer`
 * is not a type the editor knows, but it is recognizably a footer, and a tile with a
 * footer glyph is scannable in a way a wall of identical text rows is not.
 */
export type PaletteIconId =
  | 'section'
  | 'columns'
  | 'heading'
  | 'text'
  | 'image'
  | 'button'
  | 'divider'
  | 'spacer'
  | 'list'
  | 'menu'
  | 'social'
  | 'video'
  | 'quote'
  | 'card'
  | 'header'
  | 'footer'
  | 'html'
  | 'block';

/**
 * First match wins, so the order is the whole design: `header` and `footer` are tested
 * before `heading`/`text` (a "header" is not a heading, and a footer is not body copy),
 * and the generic containers come after the specific ones. `block` is the honest
 * fallback — a partner fragment named nothing in particular gets a neutral glyph rather
 * than a wrong one.
 */
const ICON_RULES: ReadonlyArray<readonly [RegExp, PaletteIconId]> = [
  [/html|markup|snippet|raw|code/, 'html'],
  [/header|masthead|topbar|preheader/, 'header'],
  [/footer|signature|imprint|legal|unsubscribe/, 'footer'],
  [/menu|nav(bar)?|links/, 'menu'],
  [/column|grid|split/, 'columns'],
  [/heading|headline|title|subject/, 'heading'],
  [/image|img|logo|picture|photo|hero|banner/, 'image'],
  [/button|cta|action/, 'button'],
  [/divider|separator|rule|line/, 'divider'],
  [/spacer|spacing|gap|whitespace/, 'spacer'],
  [/list|bullet|steps|items/, 'list'],
  [/quote|testimonial|review|voice/, 'quote'],
  // After `image`, so a hero — which is mostly a picture — keeps the image glyph.
  [/card|teaser|article|tile/, 'card'],
  [/social|share|facebook|instagram|linkedin/, 'social'],
  [/video|player|youtube/, 'video'],
  [/text|paragraph|copy|body|content/, 'text'],
  [/section|container|wrapper|row|group|card|box/, 'section'],
];

/**
 * The glyph for a block, read off its type and fragment key — the two strings that
 * actually carry intent. Nothing is declared per component, so shipping a `.twig`
 * still needs no change here (the same promise `paletteGroups` makes).
 */
export function paletteIcon(
  type: string,
  fragmentKey: string | null,
): PaletteIconId {
  const haystack = `${type} ${fragmentKey ?? ''}`.toLowerCase();
  for (const [pattern, icon] of ICON_RULES) {
    if (pattern.test(haystack)) return icon;
  }
  return 'block';
}

export interface PaletteGroup {
  id: PaletteGroupId;
  items: PaletteItem[];
}

/**
 * Presentation order within a group. Not alphabetical: it runs outside-in
 * (structure before content) and puts the blocks a marketer reaches for most
 * first. Anything not listed sorts after these, by label.
 */
const PREFERRED_ORDER = [
  // Containers first, then the composites that build a whole band in one drop,
  // then the primitives they are made of, then the chrome at either end.
  'core.section',
  'core.columns',
  'core.hero',
  'core.card',
  'core.heading',
  'core.text',
  'core.list',
  'core.image',
  'core.button',
  'core.quote',
  'core.video',
  'core.divider',
  'core.spacer',
  'core.header',
  'core.menu',
  'core.social',
  'core.footer',
];

function orderIndex(type: string): number {
  const index = PREFERRED_ORDER.indexOf(type);
  return index === -1 ? PREFERRED_ORDER.length : index;
}

/**
 * Core components split by whether they accept children; anything else that comes
 * from the fragment catalog is the partner's own.
 */
function groupOf(definition: BlockDefinition): PaletteGroupId {
  if (!definition.type.startsWith(CORE_TYPE_PREFIX)) return 'custom';
  return definition.slots.length > 0 ? 'layout' : 'content';
}

/**
 * Anything backed by a fragment, except the raw-HTML primitive (which has no
 * fragment) — so both `core/*` components and partner partials qualify.
 */
export function isPaletteCandidate(definition: BlockDefinition): boolean {
  return definition.fragmentKey !== null;
}

/**
 * The palette's contents, grouped and deterministically ordered.
 *
 * Empty groups are dropped, so an environment where the core components were
 * never seeded shows an empty palette rather than two empty headings — which is
 * the honest signal that the seeder has not run (see the 1.4 deploy hook).
 */
export function paletteGroups(
  registry: Registry,
  labels?: Record<string, string>,
): PaletteGroup[] {
  const grouped: Record<PaletteGroupId, PaletteItem[]> = {
    layout: [],
    content: [],
    custom: [],
  };

  for (const definition of Object.values(registry.definitions)) {
    if (!isPaletteCandidate(definition)) continue;

    const group = groupOf(definition);
    grouped[group].push({
      type: definition.type,
      label: resolveBlockDisplayLabel(definition, labels),
      icon: paletteIcon(definition.type, definition.fragmentKey),
      /** The exact key, so two partials with the same friendly name are still tellable apart. */
      fragmentKey: definition.fragmentKey,
      container: definition.slots.length > 0,
      inferred: definition.inferred === true,
    });
  }

  const byOrderThenLabel = (a: PaletteItem, b: PaletteItem) =>
    orderIndex(a.type) - orderIndex(b.type) || a.label.localeCompare(b.label);

  return (['layout', 'content', 'custom'] as PaletteGroupId[])
    .map((id) => ({ id, items: grouped[id].sort(byOrderThenLabel) }))
    .filter((group) => group.items.length > 0);
}
